/**
 * Performance One Pager
 *
 * Left column: KPI table + AI Summary + Next Steps
 * Right column: ROAS & Spend chart, CVR & CPC chart, CTR & CPM chart
 *
 * Reports are cached in DynamoDB. Shows a "Regenerate" button if the user
 * wants a fresh AI analysis.
 */

import { useState, useEffect, useCallback } from 'react'
import * as Icons from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { analysisApi } from './api'
import type { PerformanceReportResponse, PerformanceKPI } from './types'

// ============================================================================
// KPI Table
// ============================================================================

function StatusDot({ status }: { status: string }) {
  const color =
    status === 'good'
      ? 'bg-tf-success'
      : status === 'warning'
        ? 'bg-tf-warning'
        : 'bg-tf-critical'
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${color}`} />
}

function KPITable({ kpis }: { kpis: PerformanceKPI[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-tf-border">
            <th className="text-left py-2 pr-4 font-medium text-tf-text-muted">Metric</th>
            <th className="text-left py-2 pr-4 font-medium text-tf-text-muted">Metric Value</th>
            <th className="text-center py-2 pr-4 font-medium text-tf-text-muted">Status</th>
            <th className="text-left py-2 pr-4 font-medium text-tf-text-muted">Target</th>
            <th className="text-right py-2 font-medium text-tf-text-muted">WoW Change</th>
          </tr>
        </thead>
        <tbody>
          {kpis.map((kpi) => (
            <tr key={kpi.metric} className="border-b border-tf-border-sub">
              <td className="py-2.5 pr-4 font-medium text-tf-text">{kpi.metric}</td>
              <td className="py-2.5 pr-4 text-tf-text-sub">{kpi.formatted}</td>
              <td className="py-2.5 pr-4 text-center">
                <StatusDot status={kpi.status} />
              </td>
              <td className="py-2.5 pr-4 text-tf-text-muted">
                {kpi.target > 0 ? kpi.target.toFixed(2) : '—'}
              </td>
              <td
                className={`py-2.5 text-right font-medium ${
                  kpi.wow_change > 0
                    ? 'text-green-600'
                    : kpi.wow_change < 0
                      ? 'text-red-600'
                      : 'text-tf-text-muted'
                }`}
              >
                {kpi.wow_change > 0 ? '+' : ''}
                {kpi.wow_change.toFixed(1)}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ============================================================================
// Charts
// ============================================================================

function ROASSpendChart({ data }: { data: PerformanceReportResponse['chart_data'] }) {
  return (
    <div className="bg-tf-bg-card rounded-xl border border-tf-border p-4">
      <h4 className="text-sm font-semibold text-tf-text mb-3">ROAS & Spend</h4>
      <ResponsiveContainer width="100%" height={200}>
        <ComposedChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
          <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
          <Tooltip />
          <Legend />
          <Bar yAxisId="left" dataKey="spend" fill="#93c5fd" name="Spend" />
          <Line yAxisId="right" type="monotone" dataKey="roas" stroke="#1e40af" name="ROAS" strokeWidth={2} dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}

function DualLineChart({
  data,
  title,
  line1Key,
  line1Name,
  line1Color,
  line2Key,
  line2Name,
  line2Color,
}: {
  data: PerformanceReportResponse['chart_data']
  title: string
  line1Key: string
  line1Name: string
  line1Color: string
  line2Key: string
  line2Name: string
  line2Color: string
}) {
  return (
    <div className="bg-tf-bg-card rounded-xl border border-tf-border p-4">
      <h4 className="text-sm font-semibold text-tf-text mb-3">{title}</h4>
      <ResponsiveContainer width="100%" height={180}>
        <ComposedChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
          <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
          <Tooltip />
          <Legend />
          <Line yAxisId="left" type="monotone" dataKey={line1Key} stroke={line1Color} name={line1Name} strokeWidth={2} dot={false} />
          <Line yAxisId="right" type="monotone" dataKey={line2Key} stroke={line2Color} name={line2Name} strokeWidth={2} dot={false} strokeDasharray="5 5" />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}

// ============================================================================
// Main Component
// ============================================================================

interface PerformanceOnePagerProps {
  startDate: string
  endDate: string
}

export function PerformanceOnePager({ startDate, endDate }: PerformanceOnePagerProps) {
  const [data, setData] = useState<PerformanceReportResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [regenerating, setRegenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchReport = useCallback(async (regenerate = false) => {
    if (regenerate) {
      setRegenerating(true)
    } else {
      setLoading(true)
    }
    setError(null)
    try {
      const report = await analysisApi.getPerformanceReport(startDate, endDate, regenerate)
      setData(report)
    } catch (err) {
      console.error('Error fetching performance report:', err)
      setError('Failed to load performance report')
    } finally {
      setLoading(false)
      setRegenerating(false)
    }
  }, [startDate, endDate])

  useEffect(() => {
    fetchReport(false)
  }, [fetchReport])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Icons.Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-3 text-sm text-tf-text-muted">Generating performance analysis...</span>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <Icons.AlertCircle className="w-8 h-8 text-red-400" />
        <p className="text-sm text-red-600">{error || 'No data available'}</p>
        <button
          onClick={() => fetchReport(false)}
          className="mt-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Cache status + Regenerate button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-tf-text-muted">
          {data.from_cache ? (
            <>
              <Icons.Database className="w-3.5 h-3.5" />
              <span>Cached analysis from {new Date(data.generated_at).toLocaleString()}</span>
            </>
          ) : (
            <>
              <Icons.Sparkles className="w-3.5 h-3.5 text-primary" />
              <span>Fresh analysis generated {new Date(data.generated_at).toLocaleString()}</span>
            </>
          )}
        </div>
        <button
          onClick={() => fetchReport(true)}
          disabled={regenerating}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 border border-primary/30 rounded-lg hover:bg-primary/15 transition-colors disabled:opacity-50"
        >
          {regenerating ? (
            <>
              <Icons.Loader2 className="w-3.5 h-3.5 animate-spin" />
              Re-analyzing...
            </>
          ) : (
            <>
              <Icons.RefreshCw className="w-3.5 h-3.5" />
              Regenerate Analysis
            </>
          )}
        </button>
      </div>

      {/* Main layout */}
      <div className="flex flex-col lg:flex-row gap-6 min-h-[600px]">
        {/* Left Column: KPIs + AI Summary + Next Steps */}
        <div className="w-full lg:w-2/5 flex flex-col gap-4">
          <div className="bg-tf-bg-card rounded-xl border border-tf-border p-4">
            <KPITable kpis={data.kpis} />
          </div>

          <div>
            <h3 className="text-base font-semibold text-tf-text mb-2">Summary</h3>
            <div className="bg-tf-bg-card rounded-xl border border-tf-border p-4 prose prose-sm prose-gray max-w-none min-h-[120px]">
              <ReactMarkdown>{data.report.body || 'No summary available.'}</ReactMarkdown>
            </div>
          </div>

          {data.report.action_items && (
            <div>
              <h3 className="text-base font-semibold text-tf-text mb-2">Next Steps</h3>
              <div className="bg-tf-bg-card rounded-xl border border-tf-border p-4 prose prose-sm prose-gray max-w-none min-h-[100px]">
                <ReactMarkdown>{data.report.action_items}</ReactMarkdown>
              </div>
            </div>
          )}
        </div>

        {/* Right Column: Charts */}
        <div className="w-full lg:w-3/5 flex flex-col gap-4">
          <ROASSpendChart data={data.chart_data} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DualLineChart
              data={data.chart_data}
              title="CVR & CPC"
              line1Key="cvr"
              line1Name="CVR"
              line1Color="#16a34a"
              line2Key="cpc"
              line2Name="CPC"
              line2Color="#9333ea"
            />
            <DualLineChart
              data={data.chart_data}
              title="CTR & CPM"
              line1Key="ctr"
              line1Name="CTR"
              line1Color="#0891b2"
              line2Key="cpm"
              line2Name="CPM"
              line2Color="#dc2626"
            />
          </div>
        </div>
      </div>
    </div>
  )
}
